import { Client, ClientOptions, createClient, Event, EventListener, Sink, SubscribePayload } from 'graphql-ws'
import { ExecutionResult } from 'graphql/execution/execute'
import { Operations, SubscriptionClient as SubscriptionClientInterface } from 'subscriptions-transport-ws'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { Platform } from 'react-native'

import { rootStore } from '../root.store'

const logger = extendLogger(LoggerContexts.SubscriptionClient)

// Based on latest GraphQL Protocol:
//   https://github.com/enisdenjo/graphql-ws/blob/master/PROTOCOL.md

export enum WebSocketConnectionState {
  connecting = 'connecting',
  connected = 'connected',
  closed = 'closed',
  unknown = 'unknown'
}

// Based on latest graphql-ws events:
//   https://github.com/enisdenjo/graphql-ws/blob/master/docs/modules/client.md#event
export enum WebSocketEvents {
  closed = 'closed',
  connected = 'connected',
  connecting = 'connecting',
  error = 'error',
  message = 'message',
  ping = 'ping',
  pong = 'pong',
  unknown = 'unknown'
}

export const webSocketEvents: Event[] = [
  WebSocketEvents.connected,
  WebSocketEvents.connecting,
  WebSocketEvents.message,
  WebSocketEvents.closed,
  WebSocketEvents.error
]

const specificStatusCodeMappings = {
  '1000': 'Normal Closure',
  '1001': 'Going Away',
  '1002': 'Protocol Error',
  '1003': 'Unsupported Data',
  '1004': '(For future)',
  '1005': 'No Status Received',
  '1006': 'Abnormal Closure',
  '1007': 'Invalid frame payload data',
  '1008': 'Policy Violation',
  '1009': 'Message too big',
  '1010': 'Missing Extension',
  '1011': 'Internal Error',
  '1012': 'Service Restart',
  '1013': 'Try Again Later',
  '1014': 'Bad Gateway',
  '1015': 'TLS Handshake'
}

function getStatusCodeString(code: number) {
  if (code >= 0 && code <= 999) {
    return '(Unused)'
  }
  if (code >= 1016) {
    if (code <= 1999) {
      return '(For WebSocket standard)'
    }
    if (code <= 2999) {
      return '(For WebSocket extensions)'
    }
    if (code <= 3999) {
      return '(For libraries and frameworks)'
    }
    if (code <= 4999) {
      return '(For applications)'
    }
  }

  const codeAsEnum = code.toString() as keyof typeof specificStatusCodeMappings

  if (typeof specificStatusCodeMappings[codeAsEnum] !== 'undefined') {
    return specificStatusCodeMappings[codeAsEnum]
  }
  return '(Unknown)'
}

export interface SubscriptionClientOptions extends ClientOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on?: any
}

function isWebSocket(value: unknown): value is WebSocket {
  // logger.log('isWebSocket', typeof value)
  // biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
  return typeof value === 'object' && value !== null && value.hasOwnProperty('readyState')
}

const reconnectCode = 4205
const pongTimeout = 30 * 1000 // wait 30 seconds for the pong and then close the connection

export class SubscriptionClient implements Partial<SubscriptionClientInterface> {
  private _client: Client
  private _activeSocket?: WebSocket
  private _restartRequested = false

  public operations: Operations
  public pingSentAt: number
  public latency: number
  public timedOut?: NodeJS.Timeout

  constructor(options: SubscriptionClientOptions) {
    this.operations = {}
    this.pingSentAt = 0
    this.latency = 0

    // this._activeSocket && this._activeSocket.close(reconnectCode, 'Client Restart')
    // this._activeSocket = undefined

    let onclose = (event: CloseEvent) => {
      return
    }

    const onSocketClose = (event: CloseEvent) => {
      logger.debug('onSocketClose', event.reason, getStatusCodeString(event.code), event)
      onclose(event)
      // setup()
    }
    // let onerror = (event: Event) => {
    //   return
    // }
    // const onSocketError = (event: Event) => {
    //   logger.log('onSocketClose', event)
    //   onerror(event)
    // }

    // this._client && this.client.dispose()

    this._client = createClient({
      ...options,
      keepAlive: 10000, // ping server every 10 seconds
      retryAttempts: Infinity,
      // Client usage with custom retry timeout strategy:
      // https://the-guild.dev/graphql/ws/recipes#client-usage-with-custom-retry-timeout-strategy
      shouldRetry: () => {
        logger.log('shouldRetry')
        return true
      },
      // this is the default retryWait strategy...
      // retryWait: async function randomisedExponentialBackoff(retries: number) {
      //   let retryDelay = 1000; // start with 1s delay
      //   for (let i = 0; i < retries; i++) {
      //     retryDelay *= 2; // square `retries` times
      //   }
      //   await new Promise((resolve) =>
      //     setTimeout(
      //       // resolve pending promise with added random timeout from 300ms to 3s
      //       resolve,
      //       retryDelay + Math.floor(Math.random() * (3000 - 300) + 300),
      //     ),
      //   )
      // }
      retryWait: async function waitForServerHealthyBeforeRetry() {
        logger.debug('shouldRetry')
        // await sleep(((retries + 1) % 4) * 250)

        // if you have a server healthcheck, you can wait for it to become
        // healthy before retrying after an abrupt disconnect (most commonly a restart)
        // await waitForHealthy(url)

        // after the server becomes ready, wait for a second + random 1-4s timeout
        // (avoid DDoSing yourself) and try connecting again
        // await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 3000))

        // after the server becomes ready, wait for a second + random 200-1200ms timeout
        // (avoid DDoSing yourself) and try connecting again
        await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 200))
      },
      isFatalConnectionProblem() {
        return false
      },
      onNonLazyError: (errorOrCloseEvent: unknown) => {
        //   const onNonLazyError = `SubscriptionClient onNonLazyError ${errorOrCloseEvent}`
        logger.warn('onNonLazyError', errorOrCloseEvent)
        this.restart()
      },
      on: {
        ...options.on,
        // message: (message: any) => {
        //   logger.log('on(message)', JSON.stringify(message?.payload?.data))
        // },
        opened: (socket: WebSocket) => {
          logger.debug('on(opened)')
          this._activeSocket = socket
          if (this._activeSocket.onclose) {
            onclose = this._activeSocket.onclose
          }
          // if (this._activeSocket.onerror) {
          //   onerror = this._activeSocket.onerror
          // }
          this._activeSocket.onclose = onSocketClose
          // this._activeSocket.onerror = onSocketError
          options.on?.opened?.(socket)

          // just in case you were eager to restart
          if (this._restartRequested) {
            this._restartRequested = false
            this.restart()
          }

          // execute immediately!
          // this.send({ query: '{ hello }' })
        },

        ping: (received) => {
          if (!received /* sent */) {
            // logger.log('ping sent')
            this.pingSentAt = Date.now()
            //console.log('ping', { pingSentAt: this.pingSentAt })
            // @ts-expect-error
            this.timedOut = setTimeout(() => {
              // logger.log('ping closing socket', received)
              if (this._activeSocket?.readyState === WebSocket.OPEN) {
                //TODO: verify why the server does not trigger an pong from the server or why it takes to much time
                this._activeSocket?.close(4408, 'Request Timeout')
                //options.on?.onPing()
                //Reconnect
                rootStore?.messages?.createChannelSubscription()
              }
            }, pongTimeout) // wait pongTimeout seconds for the pong and then close the connection

            //console.log({ pingTimeout: this.timedOut })
          } else {
            logger.warn('ping not received', received)
          }
        },

        pong: (received) => {
          //console.log('pong : ', { pingSentAt: this.pingSentAt })
          if (received) {
            // logger.log('pong received', received)
            this.latency = Date.now() - this.pingSentAt
            if (this.timedOut) {
              //console.log('Clearing timeout ', this.timedOut)
              clearTimeout(this.timedOut) // pong is received, clear connection close timeout
            }
          } else {
            logger.warn('pong not received', received)
          }
        },

        connected: (event: unknown) => {
          logger.debug('on(connected)', event)
          options.on?.connected?.(event)
          logger.debug('on(connected) isWebSocket', isWebSocket(event))
          // if (isWebSocket(event)) {
          //   const socket = event as WebSocket
          //   this._activeSocket = socket
          // }
        }
      }
      // webSocketImpl: WebSocket,
      // webSocketImpl: ReconnectingWebSocket,
    })
  }

  public restart() {
    if (this._activeSocket) {
      if (this._activeSocket.readyState === WebSocket.OPEN) {
        // if the socket is still open for the restart, do the restart
        this._activeSocket.close(reconnectCode, 'Client Restart')
      } else {
        // otherwise the socket might've closed, indicate that you want
        // a restart on the next connected event
        this._restartRequested = true
      }
    }
  }

  public isSocketConnected() {
    const isSocketConnected = this._activeSocket ? this._activeSocket.readyState === WebSocket.OPEN : false
    logger.info('isSocketConnected', isSocketConnected)
    return isSocketConnected
  }

  public reconnectIfClosed() {
    const isSocketConnected = this.isSocketConnected()
    logger.debug('reconnectIfClosed isSocketConnected', isSocketConnected)
    if (this._activeSocket && !isSocketConnected) {
      logger.debug('reconnectIfClosed close and restart!')
      this._activeSocket.close(reconnectCode, 'Client Restart')
      this._restartRequested = false
      this.restart()
      return true
    }
    return false
  }

  get client() {
    return this._client
  }

  get readyState(): number {
    return this._activeSocket?.readyState ?? WebSocket.CLOSED
  }

  get socket() {
    return this._activeSocket
  }

  /**
   * Listens on the client which dispatches events about the socket state.
   */
  on<E extends Event>(event: E, listener: EventListener<E>): () => void {
    logger.debug('on', event)
    return this._client.on(event, listener)
  }

  /**
   * Subscribes through the WebSocket following the config parameters. It
   * uses the `sink` to emit received data or errors. Returns a _cleanup_
   * function used for dropping the subscription and cleaning stuff up.
   */
  subscribe<Data = Record<string, unknown>, Extensions = unknown>(
    payload: SubscribePayload,
    sink: Sink<ExecutionResult<Data, Extensions>>
  ): () => void {
    logger.debug('subscribe', payload)
    return this._client.subscribe(payload, sink)
  }

  /**
   * Terminates the WebSocket abruptly and immediately.
   *
   * A close event `4499: Terminated` is issued to the current WebSocket and an
   * artificial `{ code: 4499, reason: 'Terminated', wasClean: false }` close-event-like
   * object is immediately emitted without waiting for the one coming from `WebSocket.onclose`.
   *
   * Terminating is not considered fatal and a connection retry will occur as expected.
   *
   * Useful in cases where the WebSocket is stuck and not emitting any events;
   * can happen on iOS Safari, see: https://github.com/enisdenjo/graphql-ws/discussions/290.
   */
  terminate(): void {
    logger.debug('terminate')
    // List of valid status codes:
    // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
    this._activeSocket?.close(1000, 'Game Ended')
  }

  // public async send(payload: SubscribePayload) {
  //   logger.log('send', payload)
  //   // wait for the pong. the promise is guaranteed to settle
  //   return await new Promise<void>((resolve, reject) => {
  //     this._client.subscribe<{ data: any }>(
  //       // { query: '{ hello }' },
  //       payload,
  //       {
  //         next: (event) => {
  //           /* not interested in the pong */
  //           logger.log('send next', event)
  //           resolve(event)
  //         },
  //         error: reject,
  //         complete: resolve,
  //       }
  //     )
  //     // whatever happens to the promise, clear the pong timeout
  //   }).finally(() => {
  //     logger.log('send finally')
  //   })
  // }
}
